import {UploadImageValue} from 'platform/components';
import {match} from 'ts-pattern';
import {z} from 'zod';

import {isEmpty, isNil, isNotNil} from 'ramda';

import {
  CampaignFilter,
  FinancialProductTypeSchema,
  FinancingCompositionScene,
  PromotionalPhotoTypeSchema,
} from '@dms/api/shared';
import i18n from '@dms/i18n';

import {MAX_PROMO_PHOTO_SIZE_MB} from '../../../constants/MAX_PROMO_PHOTO_SIZE_MB';
import {VehicleSelectionMode} from '../../../types/VehicleSelectionMode';
import {compactVehicleSelectionFilter} from '../../../utils/compactVehicleSelectionFilter';
import {getDescendingVehicleSelectionRanges} from '../../../utils/getDescendingVehicleSelectionRanges';
import {hasPhotoLayer} from '../../CompositionEditor/utils/hasPhotoLayer';
import {MIN_SOURCE_PHOTO_INDEX} from '../constants/MIN_SOURCE_PHOTO_INDEX';

// `position` is intentionally not validated for uniqueness: the BE allows the same order for
// several photos. Which of the two field groups is required follows `type`.
// A cleared title or order must not abort the parse: an aborted object skips `superRefine`, which
// would hide the other errors and let `trigger(['vehicleSelectionMode', 'filter'])` pass.
export const getPromoPhotosFormSchema = (isVehicleSelectionEnabled: boolean) => {
  const requiredMessage = i18n.t('general.validations.fieldIsRequired');

  return z
    .object({
      // The text input emits null when cleared.
      title: z.preprocess((value) => value ?? '', z.string().min(1, requiredMessage)),
      // `custom` issues are fatal by default, hence `fatal: false`.
      position: z.custom<number>(
        (value) => typeof value === 'number' && value > 0,
        (value) => ({
          message: match(value)
            .with(undefined, () => requiredMessage)
            .when(
              (position) => typeof position === 'number' && !Number.isNaN(position),
              () => i18n.t('general.errors.number.min', {min: 0})
            )
            .otherwise(() => i18n.t('general.validations.mustBeNumber')),
          fatal: false,
        })
      ),
      type: PromotionalPhotoTypeSchema,
      // REGULAR
      fileId: z.custom<UploadImageValue>().nullish(),
      // FINANCING
      financialProductType: FinancialProductTypeSchema.nullable(),
      sourcePhotoIndex: z.number().nullable(),
      composition: z.custom<FinancingCompositionScene>().nullable(),
      // Both types: automatic use of the photo (matching sale vehicles / Magic Publishing).
      autoAssign: z.boolean(),
      // REGULAR only: which sale vehicles the photo applies to. `filter` keeps the conditions in the
      // "all vehicles" mode too, so switching back restores them; only "by conditions" sends them.
      vehicleSelectionMode: z.nativeEnum(VehicleSelectionMode),
      filter: z.custom<CampaignFilter>(isNotNil),
    })
    .superRefine((values, ctx) => {
      if (values.type === 'REGULAR' && isNil(values.fileId)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['fileId'],
          message: `${requiredMessage}. ${i18n.t('entity.photo.labels.maxUploadFileSize', {
            max: MAX_PROMO_PHOTO_SIZE_MB,
          })}`,
        });
      }

      if (values.type === 'FINANCING') {
        if (isNil(values.financialProductType)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['financialProductType'],
            message: requiredMessage,
          });
        }
        if (isNil(values.composition)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['composition'],
            message: i18n.t('entity.promoPhotos.validations.graphicRequired'),
          });
        }
        // Required only while the financing composition holds the photo slot it fills.
        if (hasPhotoLayer(values.composition?.layers ?? [])) {
          if (isNil(values.sourcePhotoIndex)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['sourcePhotoIndex'],
              message: requiredMessage,
            });
          } else if (values.sourcePhotoIndex < MIN_SOURCE_PHOTO_INDEX) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['sourcePhotoIndex'],
              message: i18n.t('general.validations.minValue', {min: MIN_SOURCE_PHOTO_INDEX}),
            });
          }
        }
      }

      // A REGULAR photo "by conditions" needs at least one condition and ascending ranges; the
      // errors land on the mode radio and on the upper bounds. Only the form that shows the rules
      // validates them: with the flag off a stored selection is invisible and has to round-trip as
      // it is, so an error on it could neither be seen nor fixed.
      const isByConditions =
        values.type === 'REGULAR' &&
        values.vehicleSelectionMode === VehicleSelectionMode.BY_CONDITIONS;
      if (!isVehicleSelectionEnabled || !isByConditions) {
        return;
      }
      if (isEmpty(compactVehicleSelectionFilter(values.filter))) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['vehicleSelectionMode'],
          message: i18n.t('entity.promoPhotos.validations.vehicleSelectionRequired'),
        });
      }
      getDescendingVehicleSelectionRanges(values.filter).forEach((toField) => {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['filter', toField],
          message: i18n.t('general.validations.fromTimeMustBeBeforeToTime'),
        });
      });
    });
};
